/**
 * The placing preview: the model of what I'm placing (game/build/placement.ts), see-through, on
 * the tile under me; green where it can stand, red where it can't. Any blueprint, drawn from the
 * same model tables as the placed props, so a new building needs nothing here.
 */
import * as THREE from 'three';

import { blueprintId, type Blueprint } from '@game/build/blueprints';

import { lookOf } from '../models/machines';
import type { MachinePart } from '../models/part';
import { radiusOf, type PlanetView } from '../planet/view';
import { extractorFrame, partAt } from '../props/machines';
import { tileTop } from '../props/geometry';

/** How the preview stands: where it can, where it can't, or not known yet (waiting for the server). */
export type GhostState = 'ok' | 'refused' | 'waiting';

const TINT: Readonly<Record<GhostState, number>> = {
  ok: 0x5fd08a,
  refused: 0xff6b5f,
  waiting: 0xd8dde6,
};

/** A blueprint's model: an extractor as it stands when placed (level 1), empty (its core left out). */
function modelOf(b: Blueprint): readonly MachinePart[] {
  const look = lookOf(b.type, 1);
  return look ? [...look.frame, look.turning] : [];
}

export interface Ghost {
  /** Show `blueprint` on tile `tile` of `view`, tinted by `state`; a null blueprint hides it. */
  show(view: PlanetView | null, blueprint: Blueprint | null, tile: number, state: GhostState): void;
  dispose(): void;
}

export function createGhost(parent: THREE.Object3D): Ghost {
  const group = new THREE.Group();
  group.visible = false;
  parent.add(group);
  const material = new THREE.MeshStandardMaterial({
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
    roughness: 0.45,
    metalness: 0.1,
    emissiveIntensity: 0.55,
  });
  let parts: readonly MachinePart[] = [];
  let shown = '';
  const frame = new THREE.Matrix4();

  /** The meshes of another blueprint; the geometry is the props', shared, never disposed here. */
  const build = (b: Blueprint): void => {
    group.clear();
    parts = modelOf(b);
    for (const part of parts) {
      const mesh = new THREE.Mesh(part.geometry, material);
      mesh.matrixAutoUpdate = false;
      mesh.renderOrder = 2;
      group.add(mesh);
    }
    shown = blueprintId(b);
  };

  return {
    show(view, blueprint, tile, state) {
      if (!view || !blueprint || tile < 0) {
        group.visible = false;
        return;
      }
      if (blueprintId(blueprint) !== shown) build(blueprint);
      extractorFrame(view, tile, radiusOf(view.grid.subdivisions), tileTop(view, tile), frame);
      parts.forEach((part, k) => {
        const mesh = group.children[k];
        if (mesh) partAt(frame, part, mesh.matrix);
      });
      material.color.setHex(TINT[state]);
      material.emissive.setHex(TINT[state]);
      group.visible = true;
    },
    dispose() {
      group.removeFromParent();
      material.dispose();
    },
  };
}
